import { useEffect, useRef, useState } from "react";
import {
  Circle,
  CircleDot,
  Square,
  CheckSquare,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Label } from "@fm/ui";
import type { InsuranceOption } from "@fm/api";
import type { InsuranceOptionGroup } from "@fm/api";
import {
  groupOptions,
  toggleInsuranceSelection,
  autoSelectedIds,
} from "../lib/insurance-selection";
import { EMPTY_LIST } from "../lib/stableDefaults";

export interface InsuranceOptionSelectorProps {
  options: InsuranceOption[];
  /**
   * Groups the options belong to. Omitting them renders the flat
   * choose-any list this component has always rendered, so callers that do
   * not know about groups keep working unchanged.
   */
  groups?: InsuranceOptionGroup[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  isLoading?: boolean;
  className?: string;
  currency?: string;
  /**
   * Map of optionId -> accepted terms version. The selector reports
   * acceptance changes back via `onTermsAcceptanceChange`.
   */
  acceptedTermsByOptionId?: Record<string, string | null>;
  onTermsAcceptanceChange?: (next: Record<string, string | null>) => void;
  termsAcceptanceRequired?: boolean;
}

const COVERAGE_LABEL: Record<string, string> = {
  damage_waiver: "Damage Waiver",
  liability: "Liability",
  personal_effects: "Personal Effects",
  roadside: "Roadside",
  other: "Other",
};

const UNIT_LABEL: Record<string, string> = {
  per_day: "per day",
  per_rental: "per rental",
  per_hour: "per hour",
  per_week: "per week",
  one_time: "one time",
};

/** "$1,500", with cents only when the amount is not whole. */
function formatUsd(value: string | null | undefined): string {
  const amount = parseFloat(value || "0");
  const safe = Number.isFinite(amount) ? amount : 0;

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: Number.isInteger(safe) ? 0 : 2,
  }).format(safe);
}

/**
 * Insurance option selector with terms disclosure and acceptance checkbox.
 *
 * Groups configured as "choose one" render and behave as a real radio
 * group: picking a tier clears its siblings, arrow keys move between them,
 * and a required group cannot be emptied by clicking the selected tier
 * again. The selection rules themselves live in `lib/insurance-selection`
 * so the same logic can be validated outside the component.
 */
export function InsuranceOptionSelector({
  options,
  groups = EMPTY_LIST,
  selectedIds,
  onChange,
  isLoading = false,
  className = "",
  currency = "USD",
  acceptedTermsByOptionId = {},
  onTermsAcceptanceChange,
  termsAcceptanceRequired = true,
}: InsuranceOptionSelectorProps) {
  const [expandedTerms, setExpandedTerms] = useState<Record<string, boolean>>(
    {},
  );
  const optionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Options the renter has no say over are applied up front. Inside a
  // choose-one group nothing is auto-applied: that would answer the
  // question on the renter's behalf.
  useEffect(() => {
    const next = autoSelectedIds(selectedIds, options, groups);
    if (next !== selectedIds) onChange(next);
  }, [options, groups, selectedIds, onChange]);

  const handleToggle = (option: InsuranceOption) => {
    const next = toggleInsuranceSelection(
      option.id,
      selectedIds,
      options,
      groups,
    );
    if (next === selectedIds) return;

    onChange(next);

    // Acceptance belongs to a selection; dropping one drops the other.
    if (onTermsAcceptanceChange) {
      const deselected = selectedIds.filter((id) => !next.includes(id));
      if (deselected.length > 0) {
        const acceptance = { ...acceptedTermsByOptionId };
        deselected.forEach((id) => delete acceptance[id]);
        onTermsAcceptanceChange(acceptance);
      }
    }
  };

  const handleTermsToggle = (option: InsuranceOption, accepted: boolean) => {
    if (!onTermsAcceptanceChange) return;
    const next = { ...acceptedTermsByOptionId };
    if (accepted) {
      next[option.id] = option.terms_version ?? "unknown";
    } else {
      delete next[option.id];
    }
    onTermsAcceptanceChange(next);
  };

  /** Arrow-key roving within a radio group, as the pattern requires. */
  const handleRadioKeyDown = (
    event: React.KeyboardEvent,
    bucketOptions: InsuranceOption[],
    index: number,
  ) => {
    const forward = event.key === "ArrowDown" || event.key === "ArrowRight";
    const back = event.key === "ArrowUp" || event.key === "ArrowLeft";
    if (!forward && !back) return;

    event.preventDefault();
    const delta = forward ? 1 : -1;
    const nextIndex =
      (index + delta + bucketOptions.length) % bucketOptions.length;
    const nextOption = bucketOptions[nextIndex];
    if (!nextOption) return;

    handleToggle(nextOption);
    optionRefs.current[nextOption.id]?.focus();
  };

  const formatPrice = (price: string | number, unit: string) => {
    const priceNum = typeof price === "string" ? parseFloat(price) : price;
    const formattedPrice = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(Number.isFinite(priceNum) ? priceNum : 0);

    return `${formattedPrice} ${UNIT_LABEL[unit] || unit}`;
  };

  if (isLoading) {
    return (
      <div className={`space-y-3 ${className}`}>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin h-8 w-8 border-4 border-gray-200 border-t-blue-600 rounded-full"></div>
        </div>
      </div>
    );
  }

  const buckets = groupOptions(options, groups);

  if (buckets.length === 0) {
    return (
      <div className={`text-center py-8 text-gray-500 ${className}`}>
        <p>No insurance options available</p>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {buckets.map((bucket) => {
        const isRadio = bucket.group?.selection_mode === "single";
        const headingId = `insurance-group-${bucket.group?.id ?? "ungrouped"}`;
        const selectedInBucket = bucket.options.find((o) =>
          selectedIds.includes(o.id),
        );

        return (
          <section key={headingId} className="space-y-3">
            {bucket.group && (
              <header>
                <h3
                  id={headingId}
                  className="text-sm font-semibold text-gray-900"
                >
                  {bucket.group.name}
                  {bucket.group.required && (
                    <span className="ml-1 text-red-600" aria-hidden="true">
                      *
                    </span>
                  )}
                </h3>
                <p className="text-xs text-gray-500">
                  {bucket.group.description ??
                    (isRadio ? "Choose one option." : "Choose any that apply.")}
                </p>
              </header>
            )}

            <div
              role={isRadio ? "radiogroup" : undefined}
              aria-labelledby={isRadio && bucket.group ? headingId : undefined}
              aria-required={isRadio ? bucket.group?.required : undefined}
              className="space-y-3"
            >
              {bucket.options.map((option, index) => {
                const isSelected = selectedIds.includes(option.id);
                const optionId = option.id;
                const isLocked = !isRadio && option.required;
                const hasTerms =
                  !!option.terms && option.terms.trim().length > 0;
                const isExpanded = !!expandedTerms[optionId];
                const isAccepted = !!acceptedTermsByOptionId[optionId];
                const needsAcceptance =
                  isSelected &&
                  hasTerms &&
                  termsAcceptanceRequired &&
                  !isAccepted;

                const SelectedIcon = isRadio ? CircleDot : CheckSquare;
                const EmptyIcon = isRadio ? Circle : Square;

                // Roving tabindex: the group is one tab stop, arrows move
                // inside it. Falls back to the first option when the renter
                // has not answered yet.
                const tabIndex = isRadio
                  ? (
                      selectedInBucket
                        ? selectedInBucket.id === optionId
                        : index === 0
                    )
                    ? 0
                    : -1
                  : isLocked
                    ? -1
                    : 0;

                return (
                  <div
                    key={optionId}
                    className={`rounded-lg border ${
                      isLocked
                        ? "bg-blue-50 border-blue-200"
                        : isSelected
                          ? ""
                          : "bg-white border-gray-200"
                    } ${needsAcceptance ? "ring-2 ring-amber-300" : ""}`}
                    style={{
                      borderColor:
                        isSelected && !isLocked ? "#0d3c92" : undefined,
                      backgroundColor:
                        isSelected && !isLocked ? "#f2f5fa" : undefined,
                    }}
                  >
                    <div
                      ref={(node) => {
                        optionRefs.current[optionId] = node;
                      }}
                      role={isRadio ? "radio" : "checkbox"}
                      aria-checked={isSelected}
                      aria-disabled={isLocked || undefined}
                      tabIndex={tabIndex}
                      onClick={() => !isLocked && handleToggle(option)}
                      onKeyDown={(e) => {
                        if (isLocked) return;
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleToggle(option);
                          return;
                        }
                        if (isRadio) {
                          handleRadioKeyDown(e, bucket.options, index);
                        }
                      }}
                      className={`flex items-start space-x-3 p-3 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 ${
                        isLocked ? "cursor-not-allowed" : "cursor-pointer"
                      }`}
                    >
                      <div
                        className={`flex-shrink-0 mt-0.5 ${isLocked ? "opacity-50" : ""}`}
                      >
                        {isSelected ? (
                          <SelectedIcon
                            size={22}
                            color="#083C91"
                            strokeWidth={1.75}
                            aria-hidden="true"
                          />
                        ) : (
                          <EmptyIcon
                            size={22}
                            color="#D1D5DB"
                            strokeWidth={2}
                            aria-hidden="true"
                          />
                        )}
                      </div>

                      <div className="flex-1">
                        <Label
                          className={`flex items-center justify-between ${
                            isLocked ? "cursor-not-allowed" : "cursor-pointer"
                          }`}
                        >
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-gray-900">
                                {option.name}
                              </span>
                              {option.coverage_type && (
                                <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded">
                                  {COVERAGE_LABEL[option.coverage_type] ??
                                    option.coverage_type}
                                </span>
                              )}
                              {option.waives_security_deposit && (
                                <span className="text-xs font-medium px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded">
                                  Waives deposit
                                </span>
                              )}
                              {isLocked && (
                                <span className="text-xs font-medium px-2 py-0.5 bg-red-500 text-white rounded-full">
                                  Required
                                </span>
                              )}
                            </div>
                            {option.provider_name && (
                              <p className="text-xs text-gray-500 mt-0.5">
                                Provided by {option.provider_name}
                              </p>
                            )}
                            {option.description && (
                              <p className="text-sm text-gray-500 mt-0.5">
                                {option.description}
                              </p>
                            )}
                            {(parseFloat(option.deductible || "0") > 0 ||
                              option.coverage_limit) && (
                              <div className="text-xs text-gray-600 mt-1 space-y-0.5">
                                {parseFloat(option.deductible || "0") > 0 && (
                                  <div>
                                    You pay first:{" "}
                                    <strong className="text-gray-900">
                                      {formatUsd(option.deductible)}
                                    </strong>
                                  </div>
                                )}
                                {option.coverage_limit && (
                                  <div>
                                    Insurance covers up to:{" "}
                                    <strong className="text-gray-900">
                                      {formatUsd(option.coverage_limit)}
                                    </strong>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          <div className="text-right ml-4">
                            <div className="font-semibold text-gray-900">
                              {formatPrice(option.price, option.unit)}
                            </div>
                          </div>
                        </Label>
                      </div>
                    </div>

                    {isSelected && hasTerms && (
                      <div className="border-t px-3 pb-3 pt-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedTerms((prev) => ({
                              ...prev,
                              [optionId]: !prev[optionId],
                            }));
                          }}
                          aria-expanded={isExpanded}
                          className="flex items-center gap-1 text-sm text-blue-700 hover:underline"
                        >
                          {isExpanded ? (
                            <ChevronUp size={16} aria-hidden="true" />
                          ) : (
                            <ChevronDown size={16} aria-hidden="true" />
                          )}
                          {isExpanded ? "Hide terms" : "View policy terms"}
                        </button>

                        {isExpanded && (
                          <div className="mt-2 p-3 bg-white border rounded text-xs text-gray-700 max-h-48 overflow-y-auto whitespace-pre-wrap">
                            {option.terms}
                          </div>
                        )}

                        {termsAcceptanceRequired && (
                          <label className="flex items-start gap-2 mt-3 cursor-pointer">
                            <input
                              type="checkbox"
                              className="mt-0.5"
                              checked={isAccepted}
                              onChange={(e) =>
                                handleTermsToggle(option, e.target.checked)
                              }
                            />
                            <span className="text-sm text-gray-700">
                              I have read and accept the terms of this insurance
                              policy
                            </span>
                          </label>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
